import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkAlgorithmPackageExists,
  type AlgorithmPackageExistsVo,
} from '@apis/simulation';

/** 探测状态机 */
export type ProbeStatus = 'idle' | 'checking' | 'exists' | 'missing' | 'error';

export interface ProbeState {
  status: ProbeStatus;
  /** 服务端解析出的 bos:// 全路径；用于 tooltip 显示 */
  resolvedUrl?: string;
  /** error 状态下的服务端说明（BOS 401、模板未配等） */
  message?: string;
}

const IDLE: ProbeState = { status: 'idle' };

/**
 * 版本号存在性探测 hook。
 *
 * 语义:
 * - 空 / 全空白 → idle,不发请求
 * - 值变化 → 先切 checking,``debounceMs`` 后发一次;期间新的键入会取消上一次 pending
 * - 后端 exists=true → exists;exists=false && !error → missing;有 error → error(不阻塞用户提交)
 *
 * 只做 UI 反馈,**不做**表单校验拦截。真正的字段合法性由 Form.Item rules 负责。
 * BOS 探测失败(401 / 网络) 明确回退到 error 状态,让用户知道是"探测不了"而非"包不存在"。
 */
export function usePackageExistsProbe(
  value: string | undefined,
  buildType: 'package_neodata' | 'package_bazel' = 'package_neodata',
  enabled: boolean = true,
  debounceMs: number = 500,
): ProbeState {
  const [state, setState] = useState<ProbeState>(IDLE);
  // 记录当前发出的请求序号,late-arriving response 直接丢弃防竞态
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearTimer();
    if (!enabled) {
      setState(IDLE);
      return () => clearTimer();
    }
    const raw = (value ?? '').trim();
    if (!raw) {
      setState(IDLE);
      return () => clearTimer();
    }
    setState({ status: 'checking' });
    const mySeq = ++seqRef.current;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      checkAlgorithmPackageExists(raw, buildType)
        .then((vo: AlgorithmPackageExistsVo) => {
          if (mySeq !== seqRef.current) return;
          if (vo.error) {
            setState({
              status: 'error',
              resolvedUrl: vo.resolvedUrl,
              message: vo.error,
            });
            return;
          }
          setState({
            status: vo.exists ? 'exists' : 'missing',
            resolvedUrl: vo.resolvedUrl,
          });
        })
        .catch((err: unknown) => {
          if (mySeq !== seqRef.current) return;
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : '探测失败',
          });
        });
    }, debounceMs);
    return () => clearTimer();
  }, [value, buildType, enabled, debounceMs, clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);
  return state;
}
